
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { addBreadcrumb } from '@/lib/breadcrumbs';

// Enhanced route mapping with context-aware titles
const getPageTitle = (pathname: string, searchParams?: URLSearchParams): { title: string; params?: Record<string, string> } => {
  const segments = pathname.split('/').filter(Boolean);
  const params: Record<string, string> = {};
  
  // Static routes
  if (pathname === '/') return { title: 'Home' };
  if (pathname === '/profile') return { title: 'My Profile' };
  if (pathname === '/profile/edit') return { title: 'Edit Profile' };
  if (pathname === '/matches') return { title: 'Matches' };
  if (pathname === '/matches/create') return { title: 'Create Match' };
  if (pathname === '/groups') return { title: 'Groups' };
  if (pathname === '/groups/create') return { title: 'Create Group' };
  if (pathname === '/your-groups') return { title: 'Your Groups' };
  if (pathname === '/tournaments') return { title: 'Tournaments' };
  if (pathname === '/tournaments/create') return { title: 'Create Tournament' };
  if (pathname === '/stats') return { title: 'Statistics' };
  if (pathname === '/messages') return { title: 'Messages' };
  if (pathname === '/settings') return { title: 'Settings' };
  if (pathname === '/settings/privacy') return { title: 'Privacy Settings' };
  if (pathname === '/settings/change-password') return { title: 'Change Password' };
  if (pathname === '/notifications') return { title: 'Notifications' };
  if (pathname === '/contact') return { title: 'Contact Us' };
  if (pathname === '/help') return { title: 'Help' };
  if (pathname === '/auth') return { title: 'Sign In' };
  if (pathname === '/forgot-password') return { title: 'Forgot Password' };
  if (pathname === '/reset-password') return { title: 'Reset Password' };
  if (pathname === '/terms') return { title: 'Terms of Service' };
  if (pathname === '/privacy') return { title: 'Privacy Policy' };
  
  // Dynamic routes with parameters
  if (segments[0] === 'user' && segments[1]) {
    params.userId = segments[1];
    return { title: 'User Profile', params };
  }
  
  if (segments[0] === 'groups' && segments[1]) {
    params.groupId = segments[1];
    if (segments[2] === 'edit') return { title: 'Edit Group', params };
    if (segments[2] === 'stats') return { title: 'Group Statistics', params };
    if (segments[2] === 'hall-of-fame') return { title: 'Hall of Fame', params };
    if (segments[2] === 'announcements') return { title: 'Announcements', params };
    if (segments[2] === 'create-tournament') return { title: 'Create Tournament', params };
    if (segments[2] === 'tournaments' && segments[3]) {
      params.tournamentId = segments[3];
      if (segments[4] === 'edit') return { title: 'Edit Tournament', params };
      return { title: 'Tournament Details', params };
    }
    return { title: 'Group Details', params };
  }
  
  if (segments[0] === 'tournaments' && segments[1]) {
    params.tournamentId = segments[1];
    if (segments[2] === 'edit') return { title: 'Edit Tournament', params };
    return { title: 'Tournament Details', params };
  }
  
  if (segments[0] === 'matches' && segments[1]) {
    params.matchId = segments[1];
    return { title: 'Match Details', params };
  }
  
  if (segments[0] === 'messages') {
    if (segments[1] === 'group' && segments[2]) {
      params.groupId = segments[2];
      return { title: 'Group Chat', params };
    }
    if (segments[1] === 'user' && segments[2]) {
      params.userId = segments[2];
      return { title: 'Direct Message', params };
    }
  }
  
  // Default fallback
  const title = segments[segments.length - 1]?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Page';
  return { title };
};

export const usePageTracking = () => {
  const location = useLocation();
  const { pushBreadcrumb } = useNavigation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const { title, params } = getPageTitle(location.pathname, searchParams);
    
    // Add context data for enhanced navigation
    const contextData = {
      search: location.search,
      hash: location.hash,
      state: location.state
    };
    
    pushBreadcrumb(location.pathname, title, params, contextData);

    // Error-report breadcrumb (route only, no query values)
    addBreadcrumb('navigation', `route: ${location.pathname}`, { title });
  }, [location.pathname, location.search, location.hash, pushBreadcrumb]);
};
